import React, { FC, useEffect, useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { Helmet } from 'react-helmet';
import { Redirect, useHistory } from 'react-router-dom';

import { JSBlock } from '../../components/JSBlock';
import RouteWithCode from '../../components/RouteWithCode/RouteWithCode';
import { DEFAULT_DISCOVER_URL } from '../../const';
import { GET_PAGE_BY_URL } from '../../graphql/pages';
import { LearnerInfo, Query, QueryGetPageByUrlArgs, Tenant } from '../../types';
import { CustomPage } from '../CustomPage';
import NotFoundPage from '../NotFoundPage';

const getSearchActionObj = (url) => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  url: `${url}/`,
  potentialAction: [
    {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${url}${DEFAULT_DISCOVER_URL}/search?s={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  ],
});

interface HomepageProps {
  userInfo?: LearnerInfo;
  userLoading: boolean;
  tenant?: Tenant;
}

const Homepage: FC<HomepageProps> = ({ userLoading, userInfo, tenant }) => {
  const history = useHistory();
  const location = history?.location;
  const pathname = location?.pathname;
  const queryParams = new URLSearchParams(location?.search);
  const searchValue = queryParams.get('s');
  const cleanPathname =
    pathname.length > 1 && pathname[pathname.length - 1] === '/' ? pathname.slice(0, -1) : pathname;

  const { data: pageData, loading: pageLoading } = useQuery<
    Pick<Query, 'getPageByUrl'>,
    QueryGetPageByUrlArgs
  >(GET_PAGE_BY_URL, {
    variables: {
      input: {
        url: [cleanPathname],
      },
    },
  });

  const blocks = pageData?.getPageByUrl?.blocks;
  const hasBlocks = blocks && blocks.length > 0;

  const scriptData = useMemo(
    () => ({
      customScriptType: 'application/ld+json',
      js_code: JSON.stringify(getSearchActionObj(tenant?.portal_url)),
    }),
    [tenant],
  );

  useEffect(() => {
    const queryParams = new URLSearchParams(location?.search);
    const searchValue = queryParams.get('s');
    if (searchValue) {
      history.push(`${DEFAULT_DISCOVER_URL}/search/?s=${searchValue}`);
    }
    if (!pageLoading && !hasBlocks) {
      history.push('/404');
    }
  }, [history, pageLoading, hasBlocks, location]);

  if (!searchValue && userInfo && !userLoading) {
    return <Redirect to="/home" />;
  }

  if (pageLoading) {
    return null;
  }

  if (hasBlocks) {
    return (
      <>
        {process.env.REACT_APP_BUILD_TYPE === 'prod' && (
          <Helmet>
            <link rel="canonical" href={`${tenant?.portal_url}/`} />
          </Helmet>
        )}
        <JSBlock data={scriptData} />
        <CustomPage blocks={blocks} tenant={tenant} preventLoader blocksLoading={pageLoading} />
      </>
    );
  } else {
    return (
      <RouteWithCode code={404}>
        <NotFoundPage />
      </RouteWithCode>
    );
  }
};

export default Homepage;
